import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PrimaryButton } from '../../Component/Buttons';
import { CustomFilePickerUpload } from '../../Component/FormElements';
import { APIService, AWSService } from "../../Service";

const FileOperations = () => {
    const navigate = useNavigate();
    const [uploadData, setUploadData] = useState({
        excelFile: null,
        excelFileError: '',
        fileLoader: false,
        submitLoader: false,
        objectKey: '',
        viewUrl: ''
    });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const _updateUploadData = (key, value) => {
        setUploadData(prev => ({
            ...prev,
            [key]: value
        }));
    };

    /** 🔹 Step 1: Generate presigned URL & upload file to AWS */
    const _handleUpload = async (file) => {
        if (!file) return;

        _updateUploadData('fileLoader', true);
        _updateUploadData('excelFileError', '');

        const is_post = true;
        const url = 'general/generate-presigned-url';
        const request = {
            filename: file.name,
            content_type: file.type || "application/octet-stream",
        };

        try {
            const response = await APIService(is_post, url, request);

            if (response.status_code === 200) {
                toast.dismiss();

                // Upload file using presigned URL (Content-Type must match signed URL)
                const fileResponse = await AWSService(response.data.upload_url, file);

                if (fileResponse?.statusCode === 200) {
                    _updateUploadData('excelFile', file);
                    _updateUploadData('objectKey', response.data.object_key);
                    _updateUploadData('viewUrl', response.data.view_url);
                    toast.success(response.message, {
                        type: "success",
                        className: "e-toast",
                    });
                } else {
                    toast.error(fileResponse?.message || "File upload to storage failed", {
                        type: "error",
                        className: "e-toast",
                    });
                }
            } else {
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
        } catch (error) {
            console.error("Upload error:", error);
            toast.error("An unexpected error occurred");
        } finally {
            _updateUploadData('fileLoader', false);
        }
    };

    /** 🔹 Step 2: Submit uploaded file URL to backend */
    const _handleSubmit = async () => {
        if (!uploadData.excelFile) {
            _updateUploadData('excelFileError', 'Please upload a file before submitting.');
            return;
        }

        if (!uploadData.viewUrl) {
            toast.error("Upload incomplete — missing file URL.");
            return;
        }

        _updateUploadData('submitLoader', true);

        try {
            const is_post = true;
            const url = 'general/upload';
            const request = {
                file: uploadData.viewUrl
            };

            const response = await APIService(is_post, url, request);

            if (response.status_code === 200) {
                toast.success(response.message, {
                    type: "success",
                    className: "e-toast",
                });

                // Clear uploaded file after successful submit (background processing message)
                setUploadData({
                    excelFile: null,
                    excelFileError: '',
                    fileLoader: false,
                    submitLoader: false,
                    objectKey: '',
                    viewUrl: ''
                });
                navigate("/corridor-config");
            } else {
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
                _updateUploadData('submitLoader', false);
            }
        } catch (error) {
            console.error("Submit error:", error);
            toast.error("An unexpected error occurred");
            _updateUploadData('submitLoader', false);
        }
    };

    const _handleClearFile = (fileKey, errorKey) => {
        setUploadData(prev => ({
            ...prev,
            [fileKey]: null,
            [errorKey]: '',
            objectKey: '',
            viewUrl: ''
        }));
    };

    return (
        <div className="pt-3 padding-98px-bottom padding-32px-lr">
            <div className='row'>
                <div className='col-lg-12'>
                    <h1 className='pt-3 e-primary-font e-font-weight-600 e-font-24'>
                        Upload
                    </h1>
                </div>
            </div>

            <div className='row'>
                <div className='col-lg-4 col-md-5 col-sm-6'>
                    <div id='excelFile' className="pt-2">
                        <CustomFilePickerUpload
                            label='Upload file'
                            postfix='*'
                            type='file'
                            file={uploadData.excelFile}
                            error={uploadData.excelFileError}
                            loader={uploadData.fileLoader}
                            uniqueId='excel-file-picker'
                            maxSize={20 * 1024 * 1024} // 20 MB
                            className='px-3 e-bg-light-black'
                            accept='.xlsx, .xls'
                            description="Supported: .xlsx, .xls (Max: 5 MB)"
                            sizeError={() => _updateUploadData('excelFileError', "Max. File size is 5MB")}
                            onSelectFile={(file) => _handleUpload(file)}
                            clearFile={() => _handleClearFile('excelFile', 'excelFileError')}
                        />
                    </div>
                </div>
            </div>

            <div className='row'>
                <div className='col-lg-4 col-md-5 col-sm-6'>
                    <PrimaryButton
                        name={uploadData.submitLoader ? "Submitting..." : "Submit"}
                        className="e-bg-primary-blue mt-4 w-100"
                        onPress={_handleSubmit}
                        disabled={uploadData.submitLoader || uploadData.fileLoader}
                    />
                </div>
            </div>
        </div>
    );
};

export default FileOperations;
